/**
 * AtlasGuildModule — SWG-style guilds (V): charter a guild at YOUR placed
 * guild hall, join at a hall's terminal, and ELECT the leadership.
 *
 * The SWG loop surfaced here:
 *   1. "Claim a guild hall deed" (the structures catalog faucet) → place it
 *      with the structure placement flow (a deed in the Commons).
 *   2. Stand at your hall → charter: name + <TAG>.
 *   3. Anyone at the hall may join; members open elections for the three
 *      offices (leader / warden / exchequer), stand with a pitch, and vote —
 *      one member one vote; the tally crowns the winner.
 *
 * Display-only mirror of the guild tables; every action is a reducer.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { nameFromTables } from '../lib/versona_names.js';
import { bus } from '../bus.js';

interface Ts { microsSinceUnixEpoch: bigint }
interface IdLike { isEqual(o: unknown): boolean; toHexString(): string }
interface GuildRow { guildId: bigint; hallStructureId: bigint; name: string; tag: string; founder: IdLike; leaderId: IdLike; motd: string; createdAt: Ts }
interface MemberRow { guildMemberId: bigint; guildId: bigint; identity: IdLike; title: string; joinedAt: Ts }
interface ElectionRow { guildElectionId: bigint; guildId: bigint; office: string; opensAt: Ts; closesAt: Ts; resolved: boolean; winner: IdLike }
interface CandidateRow { guildCandidateId: bigint; guildElectionId: bigint; identity: IdLike; pitch: string }
interface VoteRow { guildElectionId: bigint; voter: IdLike; candidate: IdLike }
interface StructureRow { structureId: bigint; owner: IdLike; structureType: string; posX: number; posZ: number; worldId: bigint }
interface AccountRow { identity: IdLike; username: string }

interface GuildVersonaRow { gvId: bigint; guildId: bigint; versonaId: bigint; job: string; recruitedAt: Ts }
interface GuildStoreRow { gsId: bigint; guildId: bigint; key: string; qty: number }

interface GuildReducers {
  createGuild(a: { structureId: bigint; name: string; tag: string }): Promise<void>;
  joinGuild(a: { guildId: bigint }): Promise<void>;
  leaveGuild(a: Record<string, never>): Promise<void>;
  kickGuildMember(a: { guildId: bigint; target: unknown }): Promise<void>;
  setGuildMotd(a: { guildId: bigint; motd: string }): Promise<void>;
  openGuildElection(a: { guildId: bigint; office: string }): Promise<void>;
  runForGuildOffice(a: { guildElectionId: bigint; pitch: string }): Promise<void>;
  castGuildVote(a: { guildElectionId: bigint; candidate: unknown }): Promise<void>;
  tallyGuildElection(a: { guildElectionId: bigint }): Promise<void>;
  grantDeed(a: { structureType: string }): Promise<void>;
  assignGuildJob(a: { versonaId: bigint; job: string }): Promise<void>;
  dismissGuildVersona(a: { versonaId: bigint }): Promise<void>;
  collectGuildStore(a: { key: string; qty: number }): Promise<void>;
}

/** The nine guild posts (server GUILD_JOBS mirror). */
const GUILD_JOBS = [
  'guild_guard', 'farmer', 'miner', 'crafter', 'droid_builder',
  'weapon_builder', 'greeter', 'guild_manager', 'guild_diplomat',
] as const;
const JOB_HINT: Record<string, string> = {
  guild_guard: 'patrols the hall — an unguarded storehouse loses stock',
  farmer: 'works the plot → grain + produce',
  miner: 'works the cut → ferrite ore, stone, the odd crystal',
  crafter: 'refines ore/fiber → metal ingots + fiber weave',
  droid_builder: 'ingots + crystal → circuit lattices → an Ember work droid',
  weapon_builder: 'ingots + timber → blaster barrels + weapon grips',
  greeter: 'welcomes visitors at the hall door',
  guild_manager: 'runs the floor — production ×1.5 per manager (cap ×2)',
  guild_diplomat: 'walks embassies — every member\'s town standing rises',
};

const esc = (v: string): string => v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const OFFICES = ['leader', 'warden', 'exchequer'];
const HALL_RANGE_M = 40;

export class AtlasGuildModule implements IModule {
  readonly name = 'AtlasGuild';
  private win: UiWindow | null = null;
  private body: HTMLDivElement | null = null;
  private note: HTMLDivElement | null = null;
  private timer: number | null = null;
  private subbed = false;

  private greetAt = 0;

  private greetTimer: number | null = null;
  private onKeyV: ((e: KeyboardEvent) => void) | null = null;

  init(_app: App): void {
    void _app;
    // The greeter earns their post: approaching your hall while one stands
    // duty plays a personal welcome (3-min cooldown; cheap 4 s poll).
    this.greetTimer = window.setInterval(() => {
      try {
        const conn = atlas.conn;
        if (!conn?.identity || atlas.currentWorldId === null || atlas.spaceFlight) return;
        if (Date.now() - this.greetAt < 180_000) return;
        const me = conn.identity as unknown as IdLike;
        const mine = this.tbl<MemberRow>('guildMember').find((m) => m.identity.isEqual(me));
        if (!mine) return;
        const g = this.tbl<GuildRow>('guild').find((x) => x.guildId === mine.guildId);
        const hall = g ? this.tbl<StructureRow>('structure').find((st) => st.structureId === g.hallStructureId) : undefined;
        if (!g || !hall || hall.worldId !== atlas.currentWorldId) return;
        if (Math.hypot(hall.posX - atlas.localX, hall.posZ - atlas.localZ) > 30) return;
        const greeter = this.tbl<GuildVersonaRow>('guildVersona').find((v) => v.guildId === g.guildId && v.job === 'greeter');
        if (!greeter) return;
        this.greetAt = Date.now();
        bus.emit('atlas:status', { text: `🤝 ${nameFromTables(conn, greeter.versonaId)}: “Welcome back to ${g.name}, honored member.”` });
      } catch { /* tables mid-churn */ }
    }, 4000);
    this.onKeyV = (e: KeyboardEvent): void => {
      if (e.code !== 'KeyV' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.currentWorldId === null || atlas.spaceFlight) return;
      this.toggle();
    };
    window.addEventListener('keydown', this.onKeyV);
  }

  /** (bug sweep) the module had no dispose: the 4 s table-scan poll and the V handler outlived app teardown. */
  dispose(): void {
    if (this.greetTimer !== null) { window.clearInterval(this.greetTimer); this.greetTimer = null; }
    if (this.onKeyV) { window.removeEventListener('keydown', this.onKeyV); this.onKeyV = null; }
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
  }

  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }

  private reducers(): GuildReducers | null {
    return (atlas.conn?.reducers ?? null) as unknown as GuildReducers | null;
  }

  private ensureSubs(): void {
    if (this.subbed || !atlas.conn) return;
    this.subbed = true;
    for (const q of ['SELECT * FROM guild', 'SELECT * FROM guild_member', 'SELECT * FROM guild_election',
      'SELECT * FROM guild_candidate', 'SELECT * FROM guild_vote', 'SELECT * FROM account',
      'SELECT * FROM guild_versona', 'SELECT * FROM guild_store']) {
      try { atlas.conn.subscriptionBuilder().subscribe([q]); } catch { /* pre-publish */ }
    }
  }

  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    this.ensureSubs();
    if (!this.win) {
      this.win = makeWindow({
        id: 'guilds', title: 'Guilds', icon: 'orbit',
        width: 520, height: 560, minWidth: 420, minHeight: 360, x: 140, y: 80,
      });
      const p = document.createElement('div');
      p.style.cssText = 'display:flex;flex-direction:column;height:100%;color:#dde;font:12px/1.5 system-ui';
      this.note = document.createElement('div');
      this.note.style.cssText = 'padding:6px 10px;border-bottom:1px solid #24344a;color:#8aa0b8;min-height:16px';
      p.appendChild(this.note);
      this.body = document.createElement('div');
      this.body.style.cssText = 'flex:1;overflow:auto;padding:8px 10px';
      p.appendChild(this.body);
      this.win.body.appendChild(p);
    }
    this.win.setOpen(true);
    if (this.timer === null) {
      this.timer = window.setInterval(() => {
        if (this.win?.isOpen()) this.render();
        else if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
      }, 2000);
    }
    this.render();
  }

  private say(text: string, err = false): void {
    if (this.note) { this.note.textContent = text; this.note.style.color = err ? '#e5534b' : '#8aa0b8'; }
  }

  private nameOf(id: IdLike): string {
    for (const a of this.tbl<AccountRow>('account')) if (a.identity.isEqual(id)) return a.username;
    return `${id.toHexString().slice(0, 8)}…`;
  }

  private nearbyHall(): StructureRow | null {
    let best: StructureRow | null = null;
    let bestD = HALL_RANGE_M;
    for (const s of this.tbl<StructureRow>('structure')) {
      if (s.structureType !== 'guild_hall') continue;
      if (atlas.currentWorldId !== null && s.worldId !== atlas.currentWorldId) continue;
      const d = Math.hypot(s.posX - atlas.localX, s.posZ - atlas.localZ);
      if (d < bestD) { bestD = d; best = s; }
    }
    return best;
  }

  private h(title: string): string {
    return `<div style="font:700 11px system-ui;letter-spacing:.08em;color:#8fc3ef;margin:10px 0 4px;text-transform:uppercase">${title}</div>`;
  }

  private render(): void {
    const b = this.body;
    const conn = atlas.conn;
    if (!b || !conn?.identity) return;
    const me = conn.identity as unknown as IdLike;
    const guilds = this.tbl<GuildRow>('guild');
    const members = this.tbl<MemberRow>('guildMember');
    const mine = members.find((m) => m.identity.isEqual(me));
    const myGuild = mine ? guilds.find((g) => g.guildId === mine.guildId) : undefined;
    const hall = this.nearbyHall();
    const html: string[] = [];

    if (myGuild && mine) {
      html.push(this.memberView(myGuild, mine, members.filter((m) => m.guildId === myGuild.guildId), me));
    } else {
      // Not in a guild: hall context first, then the SWG on-ramp.
      if (hall) {
        const chartered = guilds.find((g) => g.guildId === guilds.find((x) => x.hallStructureId === hall.structureId)?.guildId
          || g.hallStructureId === hall.structureId);
        if (chartered) {
          html.push(this.h('this hall'));
          html.push(`<div><b>${esc(chartered.name)}</b> &lt;${esc(chartered.tag)}&gt; holds this hall.</div>`);
          html.push(`<button data-join="${chartered.guildId}" style="margin-top:6px;padding:4px 12px">Join ${esc(chartered.name)}</button>`);
        } else if (hall.owner.isEqual(me)) {
          html.push(this.h('charter your guild'));
          html.push('<div style="opacity:.75">Your hall stands unchartered. Name your guild:</div>' +
            '<div style="display:flex;gap:6px;margin-top:6px">' +
            '<input data-gname placeholder="guild name" maxlength="24" style="flex:1;background:#0d1626;border:1px solid #2a3f55;border-radius:6px;color:#dde;padding:4px 8px">' +
            '<input data-gtag placeholder="TAG" maxlength="5" style="width:64px;background:#0d1626;border:1px solid #2a3f55;border-radius:6px;color:#dde;padding:4px 8px">' +
            `<button data-charter="${hall.structureId}" style="padding:4px 12px">Charter</button></div>`);
        } else {
          html.push('<div style="opacity:.6">This hall is unchartered — only its owner may raise a guild here.</div>');
        }
      }
      html.push(this.h('found a guild — hall, charter, elections'));
      html.push('<div style="opacity:.75">1. Claim a <b>guild hall deed</b> · 2. Place it with the structure placement flow · 3. Stand at your hall and charter.</div>');
      html.push('<button data-deed style="margin-top:6px;padding:4px 12px">Claim a guild hall deed (5 lots)</button>');
      // Directory.
      html.push(this.h(`guild directory (${guilds.length})`));
      if (guilds.length === 0) html.push('<div style="opacity:.55">No guilds yet — the first charter awaits.</div>');
      for (const g of guilds) {
        const n = members.filter((m) => m.guildId === g.guildId).length;
        html.push(`<div style="padding:3px 0;border-bottom:1px solid #16233a"><b>${esc(g.name)}</b> &lt;${esc(g.tag)}&gt;` +
          ` <span style="opacity:.65">— ${n} member${n === 1 ? '' : 's'} · led by ${esc(this.nameOf(g.leaderId))}</span></div>`);
      }
    }
    b.innerHTML = html.join('');
    this.wire(b, myGuild ?? null);
  }

  private memberView(g: GuildRow, mine: MemberRow, roster: MemberRow[], me: IdLike): string {
    const elections = this.tbl<ElectionRow>('guildElection').filter((e) => e.guildId === g.guildId);
    const candidates = this.tbl<CandidateRow>('guildCandidate');
    const votes = this.tbl<VoteRow>('guildVote');
    const html: string[] = [];
    html.push(`<div style="font:700 16px system-ui;color:#fff">${esc(g.name)} <span style="color:#9a7ef0">&lt;${esc(g.tag)}&gt;</span></div>`);
    html.push(`<div style="opacity:.7">led by ${esc(this.nameOf(g.leaderId))} · you are ${mine.title !== '' ? `the <b>${esc(mine.title)}</b>` : 'a member'}</div>`);
    if (g.motd !== '') html.push(`<div style="margin-top:4px;padding:5px 8px;border:1px solid #24344a;border-radius:6px;color:#cfe3ff">${esc(g.motd)}</div>`);
    if (mine.title === 'leader') {
      html.push('<div style="display:flex;gap:6px;margin-top:6px">' +
        '<input data-motd placeholder="message of the day" maxlength="140" style="flex:1;background:#0d1626;border:1px solid #2a3f55;border-radius:6px;color:#dde;padding:4px 8px">' +
        '<button data-setmotd style="padding:4px 10px">Set</button></div>');
    }

    html.push(this.h(`members (${roster.length})`));
    for (const m of roster) {
      const kick = (mine.title === 'leader' || mine.title === 'warden') && !m.identity.isEqual(me) && m.title !== 'leader'
        ? ` <button data-kick="${m.identity.toHexString()}" style="padding:0 8px;font-size:10px">remove</button>` : '';
      html.push(`<div style="padding:2.5px 0;border-bottom:1px solid #16233a">${esc(this.nameOf(m.identity))}` +
        `${m.title !== '' ? ` <span style="color:#d9a94e">· ${esc(m.title)}</span>` : ''}${kick}</div>`);
    }

    // ── The versona workforce: recruit at high favor, assign the nine posts ──
    const staff = this.tbl<GuildVersonaRow>('guildVersona').filter((v) => v.guildId === g.guildId);
    html.push(this.h(`versona workforce (${staff.length})`));
    if (staff.length === 0) {
      html.push('<div style="opacity:.6">No versonas serve yet. Befriend a townsperson (talks, gifts, favors) until they regard you as a <b>FRIEND</b>, then use <i>Recruit</i> in their talk pane (E).</div>');
    } else {
      const jobsHeld = new Set(staff.map((v) => v.job).filter((j) => j !== ''));
      if (!jobsHeld.has('guild_guard')) html.push('<div style="color:#e3b341;font-size:11px">⚠ No guard on duty — the storehouse loses stock every shift.</div>');
      for (const v of staff) {
        const nm = esc(nameFromTables(atlas.conn, v.versonaId));
        const opts = ['<option value="">— unassigned —</option>']
          .concat(GUILD_JOBS.map((j) => `<option value="${j}"${v.job === j ? ' selected' : ''}>${j.replace(/_/g, ' ')}</option>`))
          .join('');
        const hint = v.job !== '' ? `<div style="opacity:.55;font-size:10px;margin-left:2px">${esc(JOB_HINT[v.job] ?? '')}</div>` : '';
        html.push(`<div style="padding:3px 0;border-bottom:1px solid #16233a">` +
          `<div style="display:flex;gap:6px;align-items:center"><b style="flex:1">${nm}</b>` +
          `<select data-job="${v.versonaId}" style="background:#0d1626;border:1px solid #2a3f55;border-radius:6px;color:#dde;padding:2px 4px;font-size:11px">${opts}</select>` +
          `<button data-dismiss="${v.versonaId}" style="padding:0 8px;font-size:10px;border-color:#6b2b28;color:#f0a8a2">release</button></div>${hint}</div>`);
      }
    }

    // ── The storehouse: what the workforce produced ──
    const store = this.tbl<GuildStoreRow>('guildStore').filter((r2) => r2.guildId === g.guildId && r2.qty > 0);
    html.push(this.h(`storehouse (${store.length} kinds)`));
    if (store.length === 0) html.push('<div style="opacity:.55">Empty shelves — assign farmers and miners, and the chain fills them.</div>');
    for (const r2 of store.sort((x, y2) => (x.key < y2.key ? -1 : 1))) {
      const label = r2.key.replace(/^resource:/, '').replace(/^item:/, '⚙ ').replace(/_/g, ' ');
      html.push(`<div style="display:flex;gap:6px;align-items:center;padding:2px 0;border-bottom:1px solid #16233a">` +
        `<span style="flex:1">${esc(label)}</span><span style="opacity:.7">×${r2.qty}</span>` +
        `<button data-collect="${esc(r2.key)}" style="padding:0 8px;font-size:10px">collect</button></div>`);
    }

    html.push(this.h('elections'));
    const open = elections.filter((e) => !e.resolved);
    for (const e of open) {
      const cs = candidates.filter((c) => c.guildElectionId === e.guildElectionId);
      const vs = votes.filter((v) => v.guildElectionId === e.guildElectionId);
      const closed = Number(e.closesAt.microsSinceUnixEpoch / 1000n) <= Date.now();
      html.push(`<div style="margin:4px 0;padding:6px 8px;border:1px solid #2a3f55;border-radius:6px">` +
        `<b style="text-transform:uppercase">${esc(e.office)}</b> <span style="opacity:.6">— ${cs.length} candidate${cs.length === 1 ? '' : 's'}, ${vs.length} vote${vs.length === 1 ? '' : 's'}${closed ? ' · polls closed' : ''}</span>`);
      for (const c of cs) {
        const myVote = vs.find((v) => v.voter.isEqual(me));
        const votedThis = myVote?.candidate.isEqual(c.identity) ?? false;
        html.push(`<div style="padding:2px 0 2px 8px">${esc(this.nameOf(c.identity))}` +
          `${c.pitch !== '' ? ` <span style="opacity:.6">“${esc(c.pitch)}”</span>` : ''}` +
          (closed ? '' : ` <button data-vote="${e.guildElectionId}:${c.identity.toHexString()}" style="padding:0 8px;font-size:10px">${votedThis ? '✓ voted' : 'vote'}</button>`));
      }
      if (!closed && !cs.some((c) => c.identity.isEqual(me))) {
        html.push(`<div style="display:flex;gap:6px;margin-top:4px"><input data-pitch="${e.guildElectionId}" placeholder="your pitch" maxlength="100" style="flex:1;background:#0d1626;border:1px solid #2a3f55;border-radius:6px;color:#dde;padding:3px 8px;font-size:11px">` +
          `<button data-run="${e.guildElectionId}" style="padding:3px 10px;font-size:11px">Stand</button></div>`);
      }
      if (closed) html.push(`<button data-tally="${e.guildElectionId}" style="margin-top:4px;padding:3px 12px">Tally the votes</button>`);
      html.push('</div>');
    }
    const openOffices = OFFICES.filter((o) => !open.some((e) => e.office === o));
    if (openOffices.length > 0) {
      html.push('<div style="display:flex;gap:6px;align-items:center;margin-top:4px"><span style="opacity:.65">Open an election:</span>' +
        openOffices.map((o) => `<button data-open="${o}" style="padding:3px 10px">${o}</button>`).join('') + '</div>');
    }
    const past = elections.filter((e) => e.resolved).slice(-3);
    for (const e of past) {
      html.push(`<div style="opacity:.55;padding:2px 0">past · ${esc(e.office)} → ${esc(this.nameOf(e.winner))}</div>`);
    }
    html.push(`<div style="margin-top:10px"><button data-leave style="padding:4px 12px;border-color:#6b2b28;color:#f0a8a2">Leave ${esc(g.name)}</button></div>`);
    return html.join('');
  }

  private wire(b: HTMLDivElement, myGuild: GuildRow | null): void {
    const r = this.reducers();
    if (!r) return;
    const conn = atlas.conn;
    const go = (p: Promise<void>, okMsg: string): void => {
      p.then(() => { this.say(okMsg); this.render(); }).catch((e: Error) => this.say(String(e.message ?? e), true));
    };
    b.querySelector('[data-deed]')?.addEventListener('click', () => go(r.grantDeed({ structureType: 'guild_hall' }), 'Deed claimed — place it with the structure placement flow, then return here'));
    const charter = b.querySelector<HTMLButtonElement>('[data-charter]');
    charter?.addEventListener('click', () => {
      const name = (b.querySelector<HTMLInputElement>('[data-gname]')?.value ?? '').trim();
      const tag = (b.querySelector<HTMLInputElement>('[data-gtag]')?.value ?? '').trim();
      go(r.createGuild({ structureId: BigInt(charter.dataset.charter!), name, tag }), `Chartered ${name}`);
    });
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-join]'))) {
      el.addEventListener('click', () => go(r.joinGuild({ guildId: BigInt(el.dataset.join!) }), 'Welcome to the guild'));
    }
    b.querySelector('[data-leave]')?.addEventListener('click', () => go(r.leaveGuild({}), 'You left the guild'));
    b.querySelector('[data-setmotd]')?.addEventListener('click', () => {
      if (!myGuild) return;
      const motd = (b.querySelector<HTMLInputElement>('[data-motd]')?.value ?? '').trim();
      go(r.setGuildMotd({ guildId: myGuild.guildId, motd }), 'MOTD set');
    });
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-open]'))) {
      el.addEventListener('click', () => { if (myGuild) go(r.openGuildElection({ guildId: myGuild.guildId, office: el.dataset.open! }), `${el.dataset.open} election opened — 24 h of polling`); });
    }
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-run]'))) {
      el.addEventListener('click', () => {
        const pitch = (b.querySelector<HTMLInputElement>(`[data-pitch="${el.dataset.run}"]`)?.value ?? '').trim();
        go(r.runForGuildOffice({ guildElectionId: BigInt(el.dataset.run!), pitch }), 'You stand for office');
      });
    }
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-vote]'))) {
      el.addEventListener('click', () => {
        const [eid, hex] = el.dataset.vote!.split(':');
        const cand = this.tbl<CandidateRow>('guildCandidate').find((c) => c.guildElectionId === BigInt(eid) && c.identity.toHexString() === hex);
        if (cand) go(r.castGuildVote({ guildElectionId: BigInt(eid), candidate: cand.identity }), 'Ballot cast');
      });
    }
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-tally]'))) {
      el.addEventListener('click', () => go(r.tallyGuildElection({ guildElectionId: BigInt(el.dataset.tally!) }), 'The votes are counted'));
    }
    for (const el of Array.from(b.querySelectorAll<HTMLSelectElement>('[data-job]'))) {
      el.addEventListener('change', () => {
        go(r.assignGuildJob({ versonaId: BigInt(el.dataset.job!), job: el.value }),
          el.value === '' ? 'Post cleared' : `Assigned: ${el.value.replace(/_/g, ' ')} — they will walk to their post`);
      });
    }
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-dismiss]'))) {
      el.addEventListener('click', () => go(r.dismissGuildVersona({ versonaId: BigInt(el.dataset.dismiss!) }), 'Released from service — they walk home'));
    }
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-collect]'))) {
      el.addEventListener('click', () => go(r.collectGuildStore({ key: el.dataset.collect!, qty: 10 }), 'Drawn from the storehouse into your inventory'));
    }
    for (const el of Array.from(b.querySelectorAll<HTMLButtonElement>('[data-kick]'))) {
      el.addEventListener('click', () => {
        if (!myGuild || !conn) return;
        const target = this.tbl<MemberRow>('guildMember').find((m) => m.guildId === myGuild.guildId && m.identity.toHexString() === el.dataset.kick);
        if (target) go(r.kickGuildMember({ guildId: myGuild.guildId, target: target.identity }), 'Member removed');
      });
    }
  }
}
